import {
  Component,
  EventEmitter,
  input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  effect,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { Router } from "@angular/router";
import { Company, CompanyResult } from "@app/interface/interfaces";
import { ApiService } from "@app/services/api.service";
import { MessageService } from "@app/services/message.service";
import { MigrationService } from "@app/services/migration.service";
import { MigrationStore } from "@app/services/migration.store";
import { ToastService } from "@app/services/toast.service";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";

@Component({
  selector: "app-search-select",
  templateUrl: "./search-select.component.html",
  styleUrls: ["./search-select.component.scss"],
  standalone: false,
})
export class SearchSelectComponent implements OnInit {
  searchControl = new FormControl({ value: "", disabled: true });
  searchResults: Company[] = [];
  allCompanies: Company[] = [];
  selectedValue?: Company;
  isLoading = false;
  showAll = true;
  showAllText = "";
  searchTerm: string | null = "";
  filteredCount: number = 0;

  loading = input<boolean>(false);
  @Output() selectionChange = new EventEmitter<Company | null>();
  //@Output() loadingState = new EventEmitter<boolean | null>();

  constructor(
    private migrationService: MigrationService,
    private apiService: ApiService,
    private messageService: MessageService,
    private toastService: ToastService,
    private migrationStore: MigrationStore,
    private router: Router
  ) {
    // React to loading signal changes
    effect(() => {
      if (this.loading() === false) {
        this.searchControl.enable();
      } else {
        this.searchControl.disable();
      }
    });
  }

  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger!: MatAutocompleteTrigger;

  async ngOnInit() {
    await this.loadCompanies();

    // local search, no backend calls
    this.searchControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe((term) => {
        // Reset showAll flag when user types
        if (term !== this.searchTerm) {
          this.searchTerm = term;
          this.showAll = true;
          this.filterResults();
        }
      });

    // listen to reload messages
    this.messageService.currentReloadMessage$.subscribe(async (value) => {
      if (value === true) {
        await this.loadCompanies();
        //update also the selected value
        if (this.selectedValue) {
          const receivedCompany: Company | undefined = this.allCompanies.find(
            (company: Company) =>
              company.company_id === this.selectedValue?.company_id
          );
          if (receivedCompany) {
            this.setSelectedValue(receivedCompany);
          }
        }
      }
    });
  }

  async loadCompanies(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.loadingStateChanged(true);
      this.toogleSearchState(false);
      this.apiService.getCompanies().subscribe({
        next: (data: CompanyResult) => {
          if (data.companies && data.companies.length > 0) {
            this.allCompanies = this.migrationService.processCompanyData(
              data.companies
            );
            this.filterResults();
            this.toogleSearchState(true);
          } else {
            if (data.error) {
              this.toastService.error(
                `Error retrieving companies: ${data.error}`
              );
            }
            this.allCompanies = [];
            this.searchResults = [];
            this.toogleSearchState(false);
          }
          this.loadingStateChanged(false);
          resolve();
        },
        error: (error) => {
          this.allCompanies = [];
          this.searchResults = [];
          this.toogleSearchState(false);
          this.loadingStateChanged(false);
        },
      });
    });
  }

  displayFn = (company: Company): string => company?.company_name ?? "";

  onOptionSelected(company?: Company) {
    if (company) {
      this.searchTerm = company.company_name;
      this.setSelectedValue(company);
    }
  }

  onShowAllClick(event: MouseEvent): void {
    // Prevent the event from closing the panel or affecting the input
    event.preventDefault();
    event.stopPropagation();

    // Toggle showAll flag to show all results
    this.showAll = false;
    this.filterResults();

    // Keep the panel open
    setTimeout(() => {
      this.autocompleteTrigger.openPanel();
    });
  }

  clearSelection() {
    this.searchControl.setValue("");
    this.selectedValue = undefined;
    this.selectionChange.emit(null);
  }

  trackByIndex(index: number) {
    return index;
  }
  viewMigrationDetails() {
    if (this.selectedValue?.migration_id) {
      const route = `/migration/${this.selectedValue?.migration_id}`;
      this.router.navigate([route]);
    }
  }
  //***********PRIVATE ***************/

  private filterResults(): void {
    // Get the current input value with proper type handling
    let currentInputValue = "";

    const controlValue = this.searchControl.value;
    if (typeof controlValue === "string") {
      currentInputValue = controlValue;
    } else if (controlValue && typeof controlValue === "object") {
      // Safely access company_name with type assertion
      currentInputValue = (controlValue as Company).company_name || "";
    }

    // Filter based on the current input value
    const lowerTerm = currentInputValue.toLowerCase();

    const filtered = this.allCompanies.filter((c) =>
      c.company_name.toLowerCase().includes(lowerTerm)
    );

    this.filteredCount = filtered.length;

    if (this.showAll && filtered.length > 10) {
      // show top 10 and add a "show all" placeholder item
      this.searchResults = [...filtered.slice(0, 10)];
      this.showAllText = `Show all ${filtered.length} results...`;
    } else {
      this.searchResults = filtered;
    }
  }

  private toogleSearchState(value: boolean) {
    if (value === false) {
      this.searchControl.disable();
    }
    if (value === true) {
      this.searchControl.enable();
    }
  }

  private setSelectedValue(company?: Company) {
    this.selectedValue = company;
    this.selectionChange.emit(this.selectedValue);
  }

  private loadingStateChanged(isLoading: boolean) {
    this.isLoading = isLoading;
    this.migrationStore.setLoading(isLoading);
    //this.loadingState.emit(isLoading);
  }
}
